import { PaStatus } from "../../../../enums/paStatus";

/**
 * Determines if comments are required based on the PA Status.
 * Comments are mandatory when PA Status is "Approved" or "Denied".
 *
 * @param paStatus - The current PA status value
 * @returns {boolean} - True if comments are required, false otherwise
 */
export const isCommentsRequired = (paStatus: string): boolean => {
  const normalizedStatus = paStatus?.toLowerCase() ?? "";
  return (
    normalizedStatus === PaStatus.APPROVED.toLowerCase() ||
    normalizedStatus === PaStatus.DENIED.toLowerCase()
  );
};

/**
 * Determines if PA Status Reason is required based on the PA Status.
 * PA Status Reason is mandatory when PA Status is "Denied".
 *
 * @param paStatus - The current PA status value
 * @returns {boolean} - True if PA Status Reason is required, false otherwise
 */
export const isPaStatusReasonRequired = (paStatus: string): boolean => {
  const normalizedStatus = paStatus?.toLowerCase() ?? "";
  return normalizedStatus === PaStatus.DENIED.toLowerCase();
};

/**
 * Validates if comments are provided when required based on PA Status.
 * Throws an error if comments are missing when PA Status is "Approved" or "Denied".
 *
 * @param paStatus - The current PA status value
 * @param comments - The comments value to validate
 * @throws {Error} - Throws error if comments are required but missing
 */
export const validateCommentsForPaStatus = (
  paStatus: string,
  comments: string,
): void => {
  if (isCommentsRequired(paStatus) && (!comments || comments.trim() === "")) {
    throw new Error(
      "Comments are required when PA Status is Approved or Denied",
    );
  }
};

/**
 * Validates if PA Status Reason is provided when required (PA Status is "Denied").
 *
 * @param paStatus - The current PA status value
 * @param paStatusReason - The PA status reason value to validate
 * @throws {Error} - Throws error if PA Status Reason is required but missing
 */
export const validatePaStatusReasonForDenied = (
  paStatus: string,
  paStatusReason: string,
): void => {
  if (
    isPaStatusReasonRequired(paStatus) &&
    (!paStatusReason || paStatusReason.trim() === "")
  ) {
    throw new Error("PA Status Reason is required when PA Status is Denied");
  }
};
